import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import { Prompt, useHistory } from 'react-router-dom';

import ExternalHeader from 'pages/plasticAssessment/components/Header/Header';
import Company from 'types/Company';

import CustomLoader from '../../../../components/CustomLoader/CustomLoader';
import Kpi from '../../../../types/Kpi';
import api from '../../../../utils/api';
import { downloadFile } from '../../../../utils/download';
import { FlexContainer } from '../../styled';
import Header from './components/Header/Header';
import KpiTable from './components/KpiTable/KpiTable';
import SavingDialog from './components/SavingDialog/SavingDialog';
import useKpiData from './hooks/useKpiData';
import useKpiSavingQueue from './hooks/useKpiSavingQueue';
import useKpiTabLogic from './hooks/useKpiTabLogic';

interface Props {
    companyData: Company;
    assessmentYear: number | null;
    companyId: number;
    assessmentId: number;
    tabsRender: JSX.Element;
    fullWidthSelector: boolean;
    exportWholePdf: () => void;
    editDisabled: boolean;
    firstDataMonthLimit: any;
    setIsCompanyDialogOpen: Dispatch<SetStateAction<boolean>>;
}

const KpiTab = (props: Props) => {
    const { companyData, assessmentYear, companyId, assessmentId, tabsRender, fullWidthSelector, exportWholePdf, editDisabled, firstDataMonthLimit, setIsCompanyDialogOpen } = props;

    const history = useHistory();

    const [isLoading, setIsLoading] = useState(true);
    const [kpis, setKpis] = useState<Kpi[]>([]);

    const [isSavingDialogOpen, setIsSavingDialogOpen] = useState(false);
    const [locationTargetAfterSave, setLocationTargetAfterSave] = useState<string | null>(null);

    const { pushDataToQueue, savingStatus, retrySave, setDirtyCell } = useKpiSavingQueue({ companyId, assessmentId, setKpis });
    const { months, canAddNewMonth, addMonth } = useKpiTabLogic({ kpis, setKpis, firstDataMonthLimit });
    const { getDataForMonth, setDataForMonth, getKpiNote, setKpiNote } = useKpiData({ kpis, setKpis, setDirtyCell, pushDataToQueue });

    const getData = useCallback(() => {
        api.request('/companies/' + companyId + '/plastic-assessments/' + assessmentId + '/kpis', 'GET').then((res) => {
            setKpis(res);
            setIsLoading(false);
        });
    }, [assessmentId, companyId]);

    const exportData = useCallback((format: 'csv' | 'pdf') => {
        api.request('/companies/' + companyId + '/plastic-assessments/' + assessmentId + '/kpis/export/' + format, 'GET', null, true, 'blob').then(res => {
            downloadFile(res, 'export.' + format);
        });
    }, [assessmentId, companyId]);

    useEffect(() => {
        getData();
    }, [getData]);

    useEffect(() => {
        if (locationTargetAfterSave && savingStatus === 'success') {
            history.push(locationTargetAfterSave);
        }
    }, [locationTargetAfterSave, savingStatus, history]);

    if (isLoading) {
        return (
            <>
                <ExternalHeader companyName={companyData.name} assessmentYear={assessmentYear} setIsCompanyDialogOpen={setIsCompanyDialogOpen} />
                <FlexContainer>{tabsRender}</FlexContainer>
                <CustomLoader />
            </>
        );
    }

    return (
        <>
            <div style={{ display: 'flex', flexDirection: 'column', maxWidth: '100%', maxHeight: '100%' }}>
                <ExternalHeader companyName={companyData.name} assessmentYear={assessmentYear} setIsCompanyDialogOpen={setIsCompanyDialogOpen} />

                <Header
                    fullWidthSelector={fullWidthSelector}
                    tabsRender={tabsRender}
                    savingStatus={savingStatus}
                    exportData={exportData}
                    exportWholePdf={exportWholePdf}
                    retrySave={retrySave}
                />

                <KpiTable
                    companyId={companyId}
                    assessmentId={assessmentId}
                    kpis={kpis}
                    months={months}
                    canAddNewMonth={canAddNewMonth}
                    addMonth={addMonth}
                    getDataForMonth={getDataForMonth}
                    setDataForMonth={setDataForMonth}
                    getKpiNote={getKpiNote}
                    setKpiNote={setKpiNote}
                    setDirtyCell={setDirtyCell}
                    editDisabled={editDisabled}
                />
            </div>


            <SavingDialog
                isOpen={isSavingDialogOpen}
                savingStatus={savingStatus}
                retrySave={retrySave}
            />

            <Prompt
                when={savingStatus !== 'success'}
                message={location => {
                    setIsSavingDialogOpen(true);
                    setLocationTargetAfterSave(location.pathname);
                    return false;
                }}
            />
        </>
    );
};

export default KpiTab;
